// UPost - Landing Page
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { SecurityFeatures } from '../components/SecurityFeatures';
import { BestsellerTicker } from '../components/BestsellerTicker';
import { SUBSCRIPTION_PRICES } from '../config';
import { getABVariant, trackVariantView, trackConversion, updateABStats } from '../utils/abTest';
import { HeroAuthorSlider } from '../components/HeroAuthorSlider';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { Layout } from '../components/Layout';

export function LandingPage() {
  const [abVariant, setAbVariant] = useState(() => getABVariant());
  const navigate = useNavigate();
  
  useEffect(() => {
    trackVariantView(abVariant);
    updateABStats(abVariant, 'view');
  }, [abVariant]);

  return (
    <Layout>
      <SeoHead
        {...SEO.home}
      />
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1455390582262-044cdead277a?w=1920&h=1080&fit=crop')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} />
      
      {/* Content */}
      <div className="container mx-auto px-4 py-8 relative z-10">

        {/* Hero Author Slider - Inspiring Famous Writers */}
        <HeroAuthorSlider 
          onCtaClick={() => {
            trackConversion('hero_slider_cta', abVariant);
            updateABStats(abVariant, 'conversion');
            navigate('/register');
          }}
        />

        <div className="text-center max-w-5xl mx-auto mb-20">
          {/* New Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 border-2 border-purple-300 rounded-full mb-6">
            <span className="text-purple-900 font-semibold">✨ Complete Author Platform - Write, Protect, Publish, Sell</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-blue-600 to-green-600 leading-tight">
            {abVariant === 'A' 
              ? 'You Dream It, We Help You Build It!'
              : 'You Dream It, We Help You Write It, Protect It, Publish It, and Sell It!'
            }
          </h1>
          
          <p className="text-2xl md:text-3xl text-gray-800 font-semibold mb-6">
            From First Draft to Bestseller - All in One Place
          </p>
          
          {/* A/B Test Indicator (only visible in dev) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="fixed bottom-4 right-4 bg-black text-white px-4 py-2 rounded-lg shadow-lg z-50">
              🎯 Variant {abVariant}
            </div>
          )}
          
          <p className="text-xl md:text-2xl text-gray-700 mb-2">
            <span className="font-semibold">Our AI-powered tools help writers create professional-quality manuscripts and screenplays in industry-standard formats</span>
          </p>
          
          <p className="text-lg md:text-xl text-gray-600 mb-6">
            Get the same step-by-step system
          </p>
          
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Everything you need to go from manuscript to published author with professional screenplay materials. 
            Save <strong>$2,000+</strong> and <strong>100+ hours</strong> vs. traditional routes.
          </p>

          {/* Value Props Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10 text-left">
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 border-2 border-purple-200">
              <div className="text-3xl mb-2">✍️</div>
              <div className="font-bold text-purple-900 mb-1">AI Writing</div>
              <div className="text-sm text-gray-600">Get 3-5 versions</div>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 border-2 border-blue-200">
              <div className="text-3xl mb-2">🎬</div>
              <div className="font-bold text-blue-900 mb-1">Screenplays</div>
              <div className="text-sm text-gray-600">Netflix/Hollywood</div>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 border-2 border-red-200">
              <div className="text-3xl mb-2">🛡️</div>
              <div className="font-bold text-red-900 mb-1">Protection</div>
              <div className="text-sm text-gray-600">WGA + Copyright</div>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 border-2 border-orange-200">
              <div className="text-3xl mb-2">📚</div>
              <div className="font-bold text-orange-900 mb-1">Publishing</div>
              <div className="text-sm text-gray-600">Amazon KDP</div>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 border-2 border-green-200">
              <div className="text-3xl mb-2">💰</div>
              <div className="font-bold text-green-900 mb-1">Marketing</div>
              <div className="text-sm text-gray-600">Launch strategy</div>
            </div>
          </div>

          <div className="space-x-4">
            <Link to="/register">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                onClick={() => {
                  trackConversion('hero_cta_click', abVariant);
                  updateABStats(abVariant, 'conversion');
                  console.log(`🎯 A/B Test: Variant ${abVariant} - Hero CTA clicked`);
                }}
              >
                Start Writing Free →
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="lg" className="text-lg px-8 py-6 border-2">
                See All Services & Pricing
              </Button>
            </Link>
          </div>
        </div>

        {/* Bestseller Ticker */}
        <div className="mb-16 -mx-4">
          <BestsellerTicker />
        </div>

        {/* NEW: Book Cover Designer Showcase */}
        <div className="mb-20 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 rounded-3xl p-1 shadow-2xl">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-4xl">🎨</span>
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                AI Book Cover Designer
              </h2>
              <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full font-bold animate-pulse">
                NEW!
              </span>
            </div>
            
            <p className="text-xl text-gray-700 text-center mb-8 max-w-3xl mx-auto">
              Generate stunning, professional book covers in minutes. 
              <strong className="text-purple-600"> Kindle KDP ready</strong>. 
              No design skills needed. Save $300-800 per cover.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-purple-50 rounded-xl p-6 border-2 border-purple-200">
                <div className="text-3xl mb-3">⚡</div>
                <h3 className="font-bold text-lg mb-2">3 Minutes</h3>
                <p className="text-gray-600">Generate 3 professional cover variations instantly</p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border-2 border-pink-200">
                <div className="text-3xl mb-3">🎯</div>
                <h3 className="font-bold text-lg mb-2">8 Genres</h3>
                <p className="text-gray-600">Pre-designed templates for Romance, Thriller, Fantasy & more</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-6 border-2 border-orange-200">
                <div className="text-3xl mb-3">📚</div>
                <h3 className="font-bold text-lg mb-2">Kindle Ready</h3>
                <p className="text-gray-600">Perfect dimensions (2560x1600px) for Amazon KDP</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-6 mb-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <div className="font-bold text-xl mb-1">💰 Save $300-800 Per Cover</div>
                  <div className="text-gray-700">Professional designers charge hundreds. We charge nothing.</div>
                </div>
                <Link to="/register">
                  <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white whitespace-nowrap">
                    Try Cover Designer Free →
                  </Button>
                </Link>
              </div>
            </div>

            <div className="text-center">
              <div className="inline-flex flex-wrap justify-center gap-3 text-sm text-gray-600">
                <span className="flex items-center gap-1">✓ High-res AI generation</span>
                <span className="flex items-center gap-1">✓ Full customization</span>
                <span className="flex items-center gap-1">✓ Multiple versions</span>
                <span className="flex items-center gap-1">✓ No watermarks</span>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center max-w-5xl mx-auto">
          {/* Social Proof */}
          <div className="mt-8 flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>18,400 AI words/month free</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>

        {/* Complete Journey Section */}
        <div className="mb-24 bg-gradient-to-r from-purple-600 via-blue-600 to-green-600 rounded-3xl p-12 text-white">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Your Complete Author Journey
          </h2>
          <p className="text-xl text-center text-purple-100 mb-12 max-w-3xl mx-auto">
            Traditional publishing costs $3,000-$15,000 and takes 18-36 months. 
            With UPost, publish professionally in 30 days for under $500.
          </p>

          <div className="grid md:grid-cols-4 gap-6">
            {/* Step 1 */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20">
              <div className="text-5xl mb-4">1️⃣</div>
              <h3 className="text-2xl font-bold mb-3">Create</h3>
              <ul className="space-y-2 text-purple-100">
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>AI multi-revision (3-5 versions)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Real-time collaboration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Screenplay formatting</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="text-sm text-purple-200">Time Saved:</div>
                <div className="text-2xl font-bold">30+ hours</div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20">
              <div className="text-5xl mb-4">2️⃣</div>
              <h3 className="text-2xl font-bold mb-3">Protect</h3>
              <ul className="space-y-2 text-purple-100">
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Copyright.gov registration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>WGA Registry guidance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Professional PDF prep</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="text-sm text-purple-200">Our Service:</div>
                <div className="text-2xl font-bold">$99 (save $400)</div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20 border-2 border-yellow-400">
              <div className="flex items-center justify-between mb-4">
                <div className="text-5xl">3️⃣</div>
                <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                  NEW!
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3">Design & Publish</h3>
              <ul className="space-y-2 text-purple-100">
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span className="font-bold">🎨 AI Cover Designer</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Kindle KDP formatting</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Professional templates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Keyword optimization</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="text-sm text-purple-200">Money Saved:</div>
                <div className="text-2xl font-bold">$300-800</div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20">
              <div className="text-5xl mb-4">4️⃣</div>
              <h3 className="text-2xl font-bold mb-3">Sell</h3>
              <ul className="space-y-2 text-purple-100">
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Launch strategy guide</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Amazon AMS ads setup</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-lg">✓</span>
                  <span>Marketing best practices</span>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="text-sm text-purple-200">Guidance:</div>
                <div className="text-2xl font-bold">Included Free</div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-white/20 backdrop-blur rounded-2xl px-8 py-6">
              <div className="text-sm text-purple-200 mb-2">Total Platform Value</div>
              <div className="text-5xl font-bold mb-2">Save $2,200+</div>
              <div className="text-lg text-purple-100">vs. hiring freelancers separately</div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-12 text-center">
          <h2 className="text-4xl font-bold mb-3">Everything Writers Need in One Platform</h2>
          <p className="text-xl text-gray-600">No more juggling 10 different tools and services</p>
        </div>
        
        <div id="features" className="grid md:grid-cols-3 gap-8 mb-20">
          <Card className="backdrop-blur-sm bg-white/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🤖</span> AI Writing Assistant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-gray-700">
                <li>• Continue writing from anywhere</li>
                <li>• Rewrite and improve text</li>
                <li>• Generate plot ideas</li>
                <li>• Polish dialogue</li>
                <li>• Expand scenes</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80 border-2 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🛡️</span> Protect Your Work
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• U.S. Copyright Office guidance</li>
                <li>• WGA Registry information</li>
                <li>• Industry-proven methods</li>
                <li>• Assisted registration ($99)</li>
              </ul>
              <Link to="/copyright-protection">
                <Button variant="outline" size="sm" className="w-full border-red-300 text-red-700 hover:bg-red-50">
                  Learn How to Protect →
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">🎨</span> AI Book Cover Designer
              </CardTitle>
              <div className="inline-block bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                NEW!
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• AI-generated professional covers</li>
                <li>• Kindle KDP compliant (2560x1600)</li>
                <li>• 8 genre templates</li>
                <li>• Full customization (fonts, colors)</li>
                <li>• Save $300-800 per book</li>
              </ul>
              <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm">
                <strong className="text-purple-900">Ready to Publish:</strong>
                <p className="text-purple-800">Generate your cover in minutes, not weeks!</p>
              </div>
            </CardContent>
          </Card>

          <Card className="backdrop-blur-sm bg-white/80">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">📝</span> Professional Tools
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-gray-700">
                <li>• Novel editor</li>
                <li>• Screenplay formatter</li>
                <li>• Auto-save every 30s</li>
                <li>• Export to Word/PDF</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Section */}
        <div className="mb-24">
          <h2 className="text-4xl font-bold text-center mb-12">
            Stop Paying for 10 Different Services
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Traditional Way */}
            <Card className="border-2 border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-2xl text-red-900">❌ The Old Way</CardTitle>
                <CardDescription className="text-red-700">Piecing together multiple tools</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm">
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Writing software (Scrivener)</span>
                    <span className="font-semibold text-gray-900">$49</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">AI writing tool (Jasper)</span>
                    <span className="font-semibold text-gray-900">$49/mo</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Editor (Reedsy)</span>
                    <span className="font-semibold text-gray-900">$500-1,500</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Cover designer</span>
                    <span className="font-semibold text-gray-900">$300-800</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Kindle formatting</span>
                    <span className="font-semibold text-gray-900">$50-150</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Formatter (Vellum)</span>
                    <span className="font-semibold text-gray-900">$249</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Copyright lawyer</span>
                    <span className="font-semibold text-gray-900">$500</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Publishing consultant</span>
                    <span className="font-semibold text-gray-900">$500</span>
                  </li>
                  <li className="flex justify-between items-center pt-3 border-t border-red-300">
                    <span className="font-bold text-gray-900">Total Cost:</span>
                    <span className="text-2xl font-bold text-red-700">$2,647+</span>
                  </li>
                  <li className="flex justify-between items-center text-red-700">
                    <span className="font-semibold">Time Investment:</span>
                    <span className="font-bold">100+ hours</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* UPost Way */}
            <Card className="border-2 border-green-400 bg-gradient-to-br from-green-50 to-blue-50 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-green-900">✅ The UPost Way</CardTitle>
                <CardDescription className="text-green-700">Everything in one platform</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm">
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Pro subscription</span>
                    <span className="font-semibold text-gray-900">$29/mo</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">AI multi-revision (included)</span>
                    <span className="font-semibold text-green-600">$0</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Real-time collaboration (included)</span>
                    <span className="font-semibold text-green-600">$0</span>
                  </li>
                  <li className="flex justify-between items-center bg-purple-100 rounded p-2 -mx-2">
                    <span className="text-gray-700 font-medium">🎨 AI Cover Designer (NEW!)</span>
                    <span className="font-semibold text-green-600">$0</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Kindle formatting (included)</span>
                    <span className="font-semibold text-green-600">$0</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Copyright service</span>
                    <span className="font-semibold text-gray-900">$99</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">Publishing service</span>
                    <span className="font-semibold text-gray-900">$299</span>
                  </li>
                  <li className="flex justify-between items-center pt-3 border-t border-green-300">
                    <span className="font-bold text-gray-900">Total Cost:</span>
                    <span className="text-2xl font-bold text-green-700">$577-777</span>
                  </li>
                  <li className="flex justify-between items-center text-green-700">
                    <span className="font-semibold">Time Investment:</span>
                    <span className="font-bold">10-20 hours</span>
                  </li>
                </ul>

                <div className="mt-6 p-4 bg-green-100 border-2 border-green-400 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-900 mb-1">Save $1,870+</div>
                    <div className="text-sm text-green-700">and 80+ hours of your time</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/register">
              <Button size="lg" className="text-lg px-10 py-6 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
                Start Saving Today - Sign Up Free →
              </Button>
            </Link>
          </div>
        </div>

        {/* Security Features Section */}
        <SecurityFeatures />

        {/* Pricing Section */}
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Simple, Transparent Pricing</h2>
          <p className="text-center text-gray-600 mb-8">
            Start free or upgrade for unlimited AI words and collaboration features.{' '}
            <Link to="/pricing" className="text-purple-600 hover:text-purple-700 font-semibold underline">
              View all plans & features →
            </Link>
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="backdrop-blur-sm bg-white/80">
              <CardHeader>
                <CardTitle>Free</CardTitle>
                <CardDescription>Perfect for getting started</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold mb-4">$0<span className="text-lg font-normal text-gray-600">/month</span></div>
                <div className="mb-4">
                  <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                    No credit card required
                  </span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li>✔️ 18,400 AI words/month</li>
                  <li>✔️ Novel editor</li>
                  <li>✔️ Auto-save</li>
                  <li>✔️ Simple timestamp</li>
                  <li>✔️ Export to Word</li>
                </ul>
                <Link to="/register">
                  <Button className="w-full">Get Started Free</Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-500 relative backdrop-blur-sm bg-white/80">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                POPULAR
              </div>
              <CardHeader>
                <CardTitle>Pro</CardTitle>
                <CardDescription>For serious writers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold mb-4">${SUBSCRIPTION_PRICES.pro_monthly}<span className="text-lg font-normal text-gray-600">/month</span></div>
                <ul className="space-y-2 mb-6">
                  <li>✔️ Unlimited AI words</li>
                  <li>✔️ Screenplay editor</li>
                  <li>✔️ Version history</li>
                  <li>✔️ Priority AI processing</li>
                  <li>✔️ Export to PDF</li>
                  <li>✔️ No branding on exports</li>
                </ul>
                <Link to="/register">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700">Upgrade to Pro</Button>
                </Link>
                <p className="text-center text-sm text-gray-600 mt-2">$180/year (save 20%)</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-8">
            <Link to="/pricing">
              <Button variant="outline" size="lg" className="text-lg px-8">
                Compare All Plans & Features
              </Button>
            </Link>
          </div>
        </div>

        {/* Affiliate Program CTA */}
        <div className="max-w-5xl mx-auto mt-20">
          <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-3xl font-bold mb-4">
                    💰 Earn While You Share
                  </h3>
                  <p className="text-purple-100 mb-6">
                    Join our affiliate program and earn <strong className="text-white">20% commission on services</strong> and{' '}
                    <strong className="text-white">25% on subscriptions</strong> for all referrals.
                    Start earning passive income today!
                  </p>
                  <ul className="space-y-2 mb-6 text-purple-100">
                    <li>✓ 20% commission on one-time services ($99-$299)</li>
                    <li>✓ 25% recurring commission on subscriptions</li>
                    <li>✓ Real-time tracking dashboard</li>
                    <li>✓ Secure payouts via Stripe</li>
                  </ul>
                  <Link to="/affiliates">
                    <Button className="bg-white text-purple-600 hover:bg-gray-100" size="lg">
                      Learn More About Affiliates →
                    </Button>
                  </Link>
                </div>
                <div className="text-center">
                  <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
                    <div className="text-5xl mb-4">🎁</div>
                    <div className="text-4xl font-bold mb-2">25%</div>
                    <div className="text-purple-200 text-sm mb-4">Per Pro Referral</div>
                    <div className="text-2xl font-bold mb-2">+5,000 Words</div>
                    <div className="text-purple-200 text-sm">Per Free Signup</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-20 pt-10 border-t border-gray-200">
          <div className="mb-4">
            <Link to="/affiliates" className="text-purple-600 hover:text-purple-700 font-semibold hover:underline text-sm">
              💰 Affiliate Program - Earn Recurring Income
            </Link>
          </div>
          <p className="text-gray-600">
            Powered by{' '}
            <a 
              href="https://web-techservices.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-700 font-semibold hover:underline"
            >
              Web-TechServices.com AI
            </a>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            &copy; 2025 Web-TechServices.com AI. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 mt-3 max-w-2xl mx-auto">
            UPost is an independent writing platform not affiliated with, endorsed by, or sponsored by Amazon, Netflix, or any other third-party services mentioned. All trademarks are property of their respective owners.
          </p>
        </div>
      </div>
    </div>
    </Layout>
  );
}